class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindTottaQuantityControls();
    this.bindTottaControls();
    this.bindTottaCartCount();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = [
      ...document.querySelectorAll('#cart-icon-bubble, [data-totta-cart-trigger]'),
    ];

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.tottaDrawerBound === 'true') return;
      cartLink.dataset.tottaDrawerBound = 'true';

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open'),
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.contains('is-empty') &&
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    this.syncTottaCartCount();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bindTottaQuantityControls() {
    /*
      Kaching Collection Breaks can rewrite/merge cart lines when a tier changes.
      Dawn's default drawer quantity handler identifies items by visual line index,
      which can become stale during those rewrites.

      Intercept Totta drawer quantity actions before Dawn's quantity-input/cart.js
      handlers and update the current Shopify line-item KEY instead.
    */
    this.addEventListener(
      'click',
      (event) => {
        const quantityButton = event.target.closest(
          '.totta-cart-drawer .quantity__button[name="minus"], .totta-cart-drawer .quantity__button[name="plus"]',
        );
        const removeButton = event.target.closest(
          '.totta-cart-drawer cart-remove-button .cart-remove-button',
        );

        if (!quantityButton && !removeButton) return;

        const row = event.target.closest('[data-totta-line]');
        const input = row?.querySelector('.quantity__input');
        const lineKey = input?.dataset.quantityLineKey;

        if (!row || !input || !lineKey) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (row.dataset.tottaQtyBusy === 'true') return;

        const current = Number(input.value || 0);
        const min = Number(input.dataset.min || input.min || 0);
        const step = Number(input.step || 1);
        const max = input.max ? Number(input.max) : Infinity;

        let nextQuantity = 0;

        if (!removeButton) {
          nextQuantity =
            quantityButton.name === 'plus'
              ? current + step
              : current - step;

          nextQuantity = Math.max(min, Math.min(max, nextQuantity));
        }

        this.updateTottaLineQuantity(row, lineKey, nextQuantity);
      },
      true,
    );

    this.addEventListener(
      'change',
      (event) => {
        const input = event.target.closest('.totta-cart-drawer .quantity__input');
        if (!input) return;

        const row = input.closest('[data-totta-line]');
        const lineKey = input.dataset.quantityLineKey;

        if (!row || !lineKey) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (row.dataset.tottaQtyBusy === 'true') return;

        const min = Number(input.dataset.min || input.min || 0);
        const step = Number(input.step || 1);
        const max = input.max ? Number(input.max) : Infinity;

        let quantity = Number(input.value || min);
        quantity = Math.max(min, Math.min(max, quantity));

        if (step > 1) {
          quantity = min + Math.round((quantity - min) / step) * step;
        }

        this.updateTottaLineQuantity(row, lineKey, quantity);
      },
      true,
    );
  }

  async updateTottaLineQuantity(row, lineKey, quantity) {
    if (!row || !lineKey) return;

    row.dataset.tottaQtyBusy = 'true';
    row.style.pointerEvents = 'none';
    row.style.opacity = '.62';

    const errorBox = this.querySelector('#CartDrawer-CartErrors');
    if (errorBox) errorBox.textContent = '';

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [lineKey]: quantity,
          },
        }),
      });

      const cart = await response.json();

      if (!response.ok || cart.errors) {
        throw new Error(
          cart.description ||
          cart.message ||
          cart.errors ||
          'Cart update failed'
        );
      }

      this.updateTottaCartCount(cart.item_count || 0);
      this.classList.toggle('is-empty', (cart.item_count || 0) === 0);

      /*
        Re-render from Shopify after Kaching has recalculated the applicable
        4 / 8 / 12 / 24 tier. This also gives us the newest line-item keys.
      */
      await this.refreshTottaDrawer();
    } catch (error) {
      console.error('Totta quantity update failed.', error);

      if (errorBox) {
        errorBox.textContent =
          error.message || 'There was an error while updating your cart. Please try again.';
      } else {
        window.alert('There was an error while updating your cart. Please try again.');
      }

      await this.refreshTottaDrawer().catch(() => {});
    } finally {
      row.dataset.tottaQtyBusy = 'false';
      row.style.pointerEvents = '';
      row.style.opacity = '';
    }
  }

  bindTottaControls() {
    this.addEventListener('click', (event) => {
      const keepShopping = event.target.closest('[data-totta-keep-shopping]');
      if (keepShopping) {
        event.preventDefault();
        this.close();
        return;
      }

      const toggle = event.target.closest('[data-totta-subscribe-toggle]');
      if (toggle) {
        event.preventDefault();
        this.handleTottaSubscriptionToggle(toggle);
        return;
      }

      const planButton = event.target.closest('[data-totta-plan-id]');
      if (planButton) {
        event.preventDefault();
        this.handleTottaPlanChange(planButton);
      }
    });
  }

  bindTottaCartCount() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.tottaCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const count = event?.cartData?.item_count;

        if (typeof count === 'number') {
          this.updateTottaCartCount(count);
        } else {
          this.syncTottaCartCount();
        }
      });
    }
  }

  disconnectedCallback() {
    this.tottaCartUpdateUnsubscriber?.();
  }

  async syncTottaCartCount() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      this.updateTottaCartCount(cart.item_count || 0);
    } catch (error) {
      console.error('Totta cart count update failed.', error);
    }
  }

  updateTottaCartCount(count) {
    document.querySelectorAll('[data-totta-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count <= 0;
    });
  }

  getTottaLines() {
    return Array.from(this.querySelectorAll('[data-totta-line]'))
      .map((row) => ({
        row,
        line: Number(row.dataset.tottaLine),
        quantity: Number(row.dataset.tottaQuantity || 1),
        currentPlanId: row.dataset.tottaCurrentPlanId || '',
        planIds: (row.dataset.tottaPlanIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean),
      }))
      .filter((line) => Number.isFinite(line.line))
      .sort((a, b) => b.line - a.line);
  }

  getActiveTottaPlanId() {
    return this.querySelector('[data-totta-plan-id].is-active')?.dataset.tottaPlanId || '';
  }

  setTottaSubscriptionBusy(busy) {
    const summary = this.querySelector('.totta-cart-summary');
    if (!summary) return;

    summary.style.pointerEvents = busy ? 'none' : '';
    summary.style.opacity = busy ? '.68' : '';

    const checkout = summary.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = busy;
  }

  async handleTottaSubscriptionToggle(toggle) {
    if (toggle.dataset.busy === 'true') return;

    const turningOn = toggle.getAttribute('aria-checked') !== 'true';
    const frequency = this.querySelector('[data-totta-cart-frequency]');

    if (turningOn && frequency) {
      frequency.hidden = false;
    }

    let planId = turningOn ? this.getActiveTottaPlanId() : null;

    if (turningOn && !planId) {
      const firstPlan = this.querySelector('[data-totta-plan-id]');
      planId = firstPlan?.dataset.tottaPlanId || null;
    }

    toggle.dataset.busy = 'true';
    this.setTottaSubscriptionBusy(true);

    try {
      await this.applyTottaSellingPlanToLines(planId);

      toggle.setAttribute('aria-checked', turningOn ? 'true' : 'false');

      if (frequency) {
        frequency.hidden = !turningOn;
      }
    } catch (error) {
      console.error('Totta subscription update failed.', error);
      window.alert('We could not update the subscription option. Please try again.');
    } finally {
      toggle.dataset.busy = 'false';
      this.setTottaSubscriptionBusy(false);
    }
  }

  async handleTottaPlanChange(button) {
    const toggle = this.querySelector('[data-totta-subscribe-toggle]');
    const isOn = toggle?.getAttribute('aria-checked') === 'true';

    this.querySelectorAll('[data-totta-plan-id]').forEach((planButton) => {
      const active = planButton === button;
      planButton.classList.toggle('is-active', active);
      planButton.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (!isOn) return;

    this.setTottaSubscriptionBusy(true);

    try {
      await this.applyTottaSellingPlanToLines(button.dataset.tottaPlanId);
    } catch (error) {
      console.error('Totta subscription frequency update failed.', error);
      window.alert('We could not change the delivery frequency. Please try again.');
    } finally {
      this.setTottaSubscriptionBusy(false);
    }
  }

  async applyTottaSellingPlanToLines(planId) {
    const lines = this.getTottaLines();
    let finalCart = null;

    for (const line of lines) {
      const isRemoving = !planId;

      if (!isRemoving && !line.planIds.includes(String(planId))) {
        continue;
      }

      if (isRemoving && !line.currentPlanId) {
        continue;
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: line.line,
          quantity: line.quantity,
          selling_plan: planId || null,
        }),
      });

      const cart = await response.json();

      if (!response.ok || cart.errors) {
        throw new Error(cart.description || cart.message || cart.errors || 'Cart update failed');
      }

      finalCart = cart;
    }

    if (!finalCart) {
      finalCart = await fetch(`${window.Shopify.routes.root}cart.js`).then((response) => response.json());
    }

    this.updateTottaCartCount(finalCart.item_count || 0);
    this.classList.toggle('is-empty', (finalCart.item_count || 0) === 0);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'totta-cart-subscription',
        cartData: finalCart,
      });
    } else {
      await this.refreshTottaDrawer();
    }
  }

  async refreshTottaDrawer() {
    const response = await fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    const sourceDrawer = html.querySelector('cart-drawer');
    const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
    const currentInner = this.querySelector('#CartDrawer .drawer__inner');

    if (!sourceInner || !currentInner) return;

    /*
      Replace the complete drawer body instead of only items/footer.
      This is required when the drawer was rendered while the cart was empty:
      otherwise the old `.drawer__inner-empty` remains visible even after items
      have been added by Build Your Box.
    */
    currentInner.innerHTML = sourceInner.innerHTML;

    this.classList.toggle(
      'is-empty',
      sourceDrawer?.classList.contains('is-empty') || false,
    );

    this.querySelector('#CartDrawer-Overlay')?.addEventListener(
      'click',
      this.close.bind(this),
    );

    await this.syncTottaCartCount();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
